import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { api, downloadUrl, fmtDay, fmtWhen } from './api.js';
import { Card, Button, Stat, Progress, Pill, Field, inputCls, Empty } from './ui.jsx';
import Assistant from './Assistant.jsx';

/**
 * Notify owners: what will go out before it goes. One introductory email per
 * person listing their sections and subsections; the department's other people
 * only when ticked; people who already heard are skipped unless ticked. The
 * email is shown as the first recipient would read it.
 *
 * Rendered through a portal to <body>: the app shell transforms its content
 * pane, which re-anchors a fixed overlay to the pane and clips its top. The
 * dialog is a column (header, scrolling body, footer with Send) that never
 * exceeds the window, so the top is always in view (operator order 2026-10-29).
 */
function NotifyModal({ boot, onClose, onSent }) {
  const cycleId = boot.cycle.id;
  const [members, setMembers] = useState(false);
  const [resend, setResend] = useState(false);
  const [data, setData] = useState(null);
  const [sample, setSample] = useState(null);
  const [showMail, setShowMail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true; setData(null);
    api(`/cycles/${cycleId}/notify?members=${members ? 1 : 0}${sample ? `&sample=${encodeURIComponent(sample)}` : ''}`).then((d) => { if (alive) { if (d?.success) setData(d); else setErr(d?.message || 'Could not load'); } });
    return () => { alive = false; };
  }, [cycleId, members, sample]);
  const recipients = data?.recipients || [];
  const toSend = recipients.filter((r) => resend || !r.notified_at);
  const heard = recipients.filter((r) => r.notified_at).length;
  const count = (role) => toSend.filter((r) => r.role === role).length;
  const internal = data?.internal_due_date || boot.cycle.internal_due_date || null;
  const cms = data?.due_date || boot.cycle.due_date;
  const send = async () => {
    setBusy(true); setErr(null);
    const d = await api(`/cycles/${cycleId}/notify`, { method: 'POST', body: { include_members: members, resend } });
    setBusy(false);
    if (!d?.success) { setErr(d?.message || 'Could not send'); return; }
    setResult(d); onSent?.();
  };
  const roleWord = (r) => (r === 'accountable' ? 'Accountable' : r === 'compliance' ? 'Held by compliance' : 'Department member');
  return createPortal(
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose} role="dialog" aria-modal="true">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-3xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-3 shrink-0">
          <div>
            <h3 className="text-base font-bold text-slate-900">Notify owners</h3>
            <p className="text-xs text-slate-500 mt-0.5">One introductory email per person, listing the sections and subsections assigned to them in CMS's names. It introduces the checklist; it is not a follow-up.</p>
            <p className="text-xs text-slate-700 mt-1.5">
              {internal
                ? <>The letter asks owners to finish by the internal due date of <b>{fmtDay(internal)}</b> and states the CMS deadline of <b>{fmtDay(cms)}</b>.</>
                : <>The letter states the CMS deadline of <b>{fmtDay(cms)}</b>. To ask owners for an earlier date, set an internal due date under Dates on Overview before sending.</>}
            </p>
          </div>
          <Button kind="ghost" onClick={onClose}>Close</Button>
        </div>
        <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1 min-h-0">
          {result ? (
            <div className="text-sm space-y-2">
              <div className="text-emerald-800 font-semibold">{result.sent.length} email{result.sent.length === 1 ? '' : 's'} sent{result.skipped.length ? `, ${result.skipped.length} skipped` : ''}{result.failed.length ? `, ${result.failed.length} failed` : ''}.</div>
              {result.sent.length > 0 && <ul className="text-slate-700 space-y-0.5">{result.sent.map((r) => <li key={r.email}>{r.name} · {r.role === 'accountable' ? 'accountable' : r.role === 'compliance' ? 'held by compliance' : 'department member'} · {r.items} item{r.items === 1 ? '' : 's'}</li>)}</ul>}
              {result.failed.length > 0 && <ul className="text-rose-700 space-y-0.5">{result.failed.map((r) => <li key={r.email}>{r.name}: {r.error}</li>)}</ul>}
              <div className="text-xs text-slate-500">Each email is kept under Correspondence. Reminders now run on the clock in Setup ({(data?.reminder_days || []).join(', ')} days before {fmtDay(data?.working_due_date || data?.due_date)}), only for what is still open.</div>
            </div>
          ) : (
            <>
              <div className="rounded-xl border border-slate-200 text-sm">
                <div className="px-4 py-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">1. Who receives it</div>
                  <p className="text-slate-700 mt-1">The accountable person for each subsection receives the email. A requirement with no reachable owner goes to the compliance officer, who holds it until an owner is assigned.</p>
                  <label className="flex items-start gap-2 cursor-pointer mt-3">
                    <input type="checkbox" className="mt-0.5 rounded border-slate-300 text-orange-600" checked={members} onChange={(e) => setMembers(e.target.checked)} />
                    <span><b className="text-slate-800">Include every member of the departments</b><span className="block text-xs text-slate-500">Every other person in a department on the checklist also receives the email, listing the items their department holds. Accountable people are always included.</span></span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer mt-2.5">
                    <input type="checkbox" className="mt-0.5 rounded border-slate-300 text-orange-600" checked={resend} onChange={(e) => setResend(e.target.checked)} />
                    <span><b className="text-slate-800">Resend to people already notified</b><span className="block text-xs text-slate-500">{heard ? `${heard} ${heard === 1 ? 'person has' : 'people have'} already received this introduction and ${heard === 1 ? 'is' : 'are'} skipped unless you tick this.` : 'Nobody has received this introduction yet.'}</span></span>
                  </label>
                </div>
              </div>
              {!data ? <Empty>Working out who gets what…</Empty> : (
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">2. The recipients</div>
                  <div className="text-sm text-slate-800 mb-2">
                    <b>Going to {toSend.length} {toSend.length === 1 ? 'person' : 'people'}</b>
                    {toSend.length > 0 && <>: {count('accountable')} accountable, {count('member')} department member{count('member') === 1 ? '' : 's'}, {count('compliance')} held by compliance.</>}
                    {!resend && heard > 0 && <span className="text-slate-500"> {recipients.length - toSend.length} already notified and skipped.</span>}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-100"><th className="py-2 pr-3 font-semibold">Person</th><th className="py-2 pr-3 font-semibold">Role</th><th className="py-2 pr-3 font-semibold">Department</th><th className="py-2 pr-3 font-semibold">Subsections</th><th className="py-2 pr-3 font-semibold">Items</th><th className="py-2 pr-3 font-semibold">Notified</th><th className="py-2 font-semibold">Email</th></tr></thead>
                      <tbody>
                        {recipients.map((r) => (
                          <tr key={r.user_id} className={`border-b border-slate-50 ${resend || !r.notified_at ? '' : 'opacity-50'}`}>
                            <td className="py-1.5 pr-3 font-semibold text-slate-800">{r.name}<div className="text-[11px] font-normal text-slate-400">{r.email}</div></td>
                            <td className="py-1.5 pr-3 text-slate-700">{r.role === 'compliance' ? <span className="text-orange-800 font-semibold" title={(r.held || []).map((h) => `${h.cms_ref}: ${h.why}`).join('\n')}>Held by compliance ({(r.held || []).length})</span> : roleWord(r.role)}</td>
                            <td className="py-1.5 pr-3 text-slate-700">{(r.departments || []).join(', ') || '—'}</td>
                            <td className="py-1.5 pr-3 tabular-nums" title={(r.sections || []).join('\n')}>{r.subsections}</td>
                            <td className="py-1.5 pr-3 tabular-nums">{r.items}</td>
                            <td className="py-1.5 pr-3 text-xs text-slate-500">{r.notified_at ? fmtDay(r.notified_at) : <span className="text-orange-800 font-semibold">not yet</span>}</td>
                            <td className="py-1.5"><button type="button" className="text-xs font-semibold text-orange-800 underline" onClick={() => { setSample(r.user_id); setShowMail(true); }}>Read</button></td>
                          </tr>
                        ))}
                        {recipients.length === 0 && <tr><td colSpan={7}><Empty>No one to notify: every open item needs an accountable person with an email, or a compliance officer with an email to hold it. Check Owners and the compliance officer's account.</Empty></td></tr>}
                      </tbody>
                    </table>
                  </div>
                  {recipients.some((r) => r.role === 'compliance') && (
                    <div className="mt-3 text-xs text-slate-700 bg-orange-50 border border-orange-200 rounded-xl px-3 py-2">
                      <b>Held by compliance.</b> These requirements have no accountable person who can be reached, so the compliance officer receives them and their reminders until an owner is assigned under Owners:
                      <ul className="mt-1 space-y-0.5">{recipients.filter((r) => r.role === 'compliance').flatMap((r) => r.held || []).filter((h, i, a) => a.findIndex((x) => x.cms_ref === h.cms_ref) === i).map((h) => <li key={h.cms_ref}><b>{h.cms_ref}</b>: {h.why}</li>)}</ul>
                    </div>
                  )}
                  {(data.unreachable || []).length > 0 && (
                    <div className="mt-3 text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
                      <b>Nobody can be told about {data.unreachable.length} requirement{data.unreachable.length === 1 ? '' : 's'}:</b> no reachable owner and no compliance officer with an email on file. {data.unreachable.map((u) => u.cms_ref).join(', ')}.
                    </div>
                  )}
                </div>
              )}
              {data?.sample && (
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">3. The email</div>
                  {showMail ? (
                    <div className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className="px-3 py-2 bg-slate-50 text-xs text-slate-600 flex items-center justify-between gap-3 flex-wrap"><span>As <b>{data.sample.for}</b> will read it · subject: {data.sample.subject}{Array.isArray(data.sample.attachments) && data.sample.attachments.length ? <> · attached: {data.sample.attachments.join(', ')}</> : null}</span><button type="button" className="underline" onClick={() => setShowMail(false)}>Hide</button></div>
                      <iframe title="The email" srcDoc={data.sample.html} className="w-full h-[28rem] bg-white" sandbox="" />
                    </div>
                  ) : (
                    <p className="text-sm text-slate-700">Each person receives the same letter with their own sections and subsections. It names the sender on behalf of the Compliance Department, states the due date{internal ? ' and the CMS deadline' : ''}, and attaches the owner guide and the person's workbook. <button type="button" className="font-semibold text-orange-800 underline" onClick={() => setShowMail(true)}>Read it as {data.sample.for} will</button>.</p>
                  )}
                </div>
              )}
              {err && <div className="text-xs text-rose-600">{err}</div>}
            </>
          )}
        </div>
        {!result && (
          <div className="px-6 py-3 border-t border-slate-100 flex items-center gap-3 flex-wrap shrink-0">
            <Button onClick={send} disabled={busy || !data || toSend.length === 0}>{busy ? 'Sending…' : `Send to ${toSend.length} ${toSend.length === 1 ? 'person' : 'people'}`}</Button>
            <span className="text-xs text-slate-500">{data?.history?.intro ? `${data.history.intro} introduction${data.history.intro === 1 ? '' : 's'} sent so far` : 'Nothing has been sent yet.'}{data?.history?.reminders ? ` · ${data.history.reminders} reminder${data.history.reminders === 1 ? '' : 's'}` : ''}</span>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

/**
 * The two dates of the year (operator order 2026-10-29): the CMS deadline set
 * when the year was opened, and the internal due date compliance asks the
 * owners to finish by. The owner letter states both; items that still carry
 * the previous working date follow the internal date, as does the clock.
 */
function DatesCard({ boot, onRefresh }) {
  const cycle = boot.cycle;
  const day = (d) => (d ? String(d).slice(0, 10) : '');
  const [value, setValue] = useState(day(cycle.internal_due_date));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  useEffect(() => { setValue(day(cycle.internal_due_date)); }, [cycle.internal_due_date]);
  const save = async (next) => {
    setBusy(true); setMsg(null);
    const d = await api(`/cycles/${cycle.id}`, { method: 'PATCH', body: { internal_due_date: next || null } });
    setBusy(false);
    if (!d?.success) { setMsg({ bad: true, text: d?.message || 'Could not save the date' }); return; }
    setMsg({ text: next ? `Internal due date set to ${fmtDay(next)}. ${d.items_moved} item${d.items_moved === 1 ? '' : 's'} now due that day.` : `Internal due date cleared. ${d.items_moved} item${d.items_moved === 1 ? '' : 's'} back on the CMS deadline.` });
    onRefresh?.();
  };
  const current = day(cycle.internal_due_date);
  return (
    <Card title="Dates" right={<span className="text-xs text-slate-500">the letter states both</span>}>
      <div className="flex items-end gap-4 flex-wrap text-sm">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">CMS deadline</div>
          <div className="text-slate-900 font-semibold py-2">{fmtDay(cycle.due_date)}</div>
        </div>
        <Field label="Internal due date" className="w-48"><input type="date" className={inputCls} value={value} max={day(cycle.due_date)} onChange={(e) => setValue(e.target.value)} /></Field>
        <Button onClick={() => save(value)} disabled={busy || !value || value === current}>{busy ? 'Saving…' : 'Save'}</Button>
        {current && <Button kind="secondary" onClick={() => save('')} disabled={busy}>Clear</Button>}
      </div>
      <p className="text-xs text-slate-500 mt-2">
        {current
          ? <>Owners are asked to finish by <b>{fmtDay(current)}</b>, ahead of the CMS deadline of {fmtDay(cycle.due_date)}. The owner letter states both dates; My items and the reminder clock follow the internal date.</>
          : <>No internal due date is set, so owners are asked to finish by the CMS deadline. Set an earlier date to leave time for review; the owner letter will state both dates, and the items and the reminder clock will follow the internal date.</>}
        {' '}An item whose date was set by hand on its page keeps that date.
      </p>
      {msg && <div className={`text-xs mt-2 ${msg.bad ? 'text-rose-600' : 'text-emerald-700'}`}>{msg.text}</div>}
    </Card>
  );
}

/** Questions owners asked compliance at the subsection level, answered here; the asker hears back by bell and email. */
function QuestionsCard({ boot, onRefresh }) {
  const [list, setList] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(null);
  const [showAnswered, setShowAnswered] = useState(false);
  const [err, setErr] = useState(null);
  const load = () => api('/questions').then((d) => setList(d?.success ? d.questions : []));
  useEffect(() => { load(); }, [boot.cycle?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const open = (list || []).filter((q) => q.status === 'open');
  const answered = (list || []).filter((q) => q.status !== 'open');
  const answer = async (q) => {
    const text = String(drafts[q.id] || '').trim(); if (!text) return;
    setBusy(q.id); setErr(null);
    const d = await api(`/questions/${q.id}/answer`, { method: 'POST', body: { answer: text } });
    setBusy(null);
    if (!d?.success) { setErr(d?.message || 'Could not answer'); return; }
    setDrafts((x) => ({ ...x, [q.id]: '' })); await load(); onRefresh?.();
  };
  return (
    <Card title="Questions from owners" tone={open.length ? 'border-orange-200' : ''} right={<span className="text-xs text-slate-500">{open.length ? `${open.length} waiting on you` : 'nothing waiting'}{answered.length ? <> · <button type="button" className="underline" onClick={() => setShowAnswered((s) => !s)}>{showAnswered ? 'hide' : 'show'} {answered.length} answered</button></> : null}</span>}>
      {list === null ? <Empty>Loading…</Empty> : open.length === 0 && !showAnswered ? <Empty>No open questions. Owners ask from "Ask compliance" on a subsection under My items; the question lands here and on the team's bell and email.</Empty> : (
        <div className="space-y-3">
          {(showAnswered ? [...open, ...answered] : open).map((q) => (
            <div key={q.id} className={`rounded-xl border px-3 py-2.5 text-sm ${q.status === 'open' ? 'border-orange-200 bg-orange-50/30' : 'border-slate-200'}`}>
              <div className="flex items-center gap-2 flex-wrap text-[11px] text-slate-500">
                <Pill tone={q.status === 'open' ? 'bg-orange-50 text-orange-800 ring-orange-200' : 'bg-emerald-50 text-emerald-700 ring-emerald-200'}>{q.status === 'open' ? 'Open' : 'Answered'}</Pill>
                {q.kind === 'reassign' && <Pill tone="bg-violet-50 text-violet-800 ring-violet-200">Reassignment request</Pill>}
                <b className="text-slate-700">{q.asked_by_name || q.asked_by_email}</b><span>· {fmtWhen(q.created_at)}</span>
              </div>
              <div className="text-xs font-semibold text-slate-700 mt-1">{q.subsection}{q.item_cms_ref ? ` · ${q.item_cms_ref}` : ''}</div>
              <div className="text-slate-900 whitespace-pre-wrap mt-1">{q.body}</div>
              {q.kind === 'reassign' && q.status === 'open' && <div className="text-xs text-violet-800 mt-1">Reassign from the item page (Departments and accountable people), then answer here so the asker is told.</div>}
              {q.status === 'open' ? (
                <div className="mt-2 flex items-end gap-2">
                  <textarea rows={2} className={inputCls} placeholder="Answer in plain words. The asker gets it by bell and email." value={drafts[q.id] || ''} onChange={(e) => setDrafts((x) => ({ ...x, [q.id]: e.target.value }))} />
                  <Button kind="good" disabled={busy === q.id || !String(drafts[q.id] || '').trim()} onClick={() => answer(q)}>{busy === q.id ? 'Sending…' : 'Answer'}</Button>
                </div>
              ) : (
                <div className="mt-2 border-l-2 border-emerald-400 pl-3"><div className="text-[11px] text-slate-500">{q.answered_by_name || 'Compliance'} · {fmtWhen(q.answered_at)}</div><div className="text-slate-800 whitespace-pre-wrap">{q.answer}</div></div>
              )}
            </div>
          ))}
          {err && <div className="text-xs text-rose-600">{err}</div>}
        </div>
      )}
    </Card>
  );
}

/** "Do we already have this?" for the whole year: one background run over every open item, progress shown while it reads the library. */
function PolicySweep({ boot, onRefresh }) {
  const [run, setRun] = useState(boot.policy_run || null);
  const [err, setErr] = useState(null);
  const cycleId = boot.cycle?.id;
  const running = run && run.status === 'generating';
  useEffect(() => {
    if (!running || !cycleId) return undefined;
    const t = setInterval(async () => {
      const d = await api(`/cycles/${cycleId}/policy-check`);
      if (d?.success) { setRun(d.run); if (d.run?.status !== 'generating') { clearInterval(t); onRefresh?.(); } }
    }, 4000);
    return () => clearInterval(t);
  }, [running, cycleId, onRefresh]);
  const start = async () => {
    setErr(null);
    const d = await api(`/cycles/${cycleId}/policy-check`, { method: 'POST', body: {} });
    if (!d?.success) { setErr(d?.message || 'Could not start'); return; }
    setRun(d.run);
  };
  const p = run?.progress || {}; const r = run?.result || {};
  return (
    <div className="mt-4 pt-3 border-t border-slate-100 text-sm">
      <div className="flex items-center gap-3 flex-wrap">
        <Button kind="secondary" disabled={!cycleId || running} onClick={start}>{running ? 'Checking policies…' : 'Check all open items against our policies'}</Button>
        {running && <span className="text-xs text-slate-600">{p.done || 0} of {p.total || '…'} items read · {p.found || 0} matches so far</span>}
        {!running && run?.status === 'ready' && <span className="text-xs text-slate-600">Last run: {r.items} items read, {r.found} polic{r.found === 1 ? 'y' : 'ies'} matched on {r.items_with_proposals} item{r.items_with_proposals === 1 ? '' : 's'}. Decide on each item.</span>}
        {!running && run?.status === 'failed' && <span className="text-xs text-rose-700">The last run failed: {run.error_message}</span>}
        {err && <span className="text-xs text-rose-700">{err}</span>}
      </div>
      {running && <div className="mt-2 max-w-md"><Progress pct={p.total ? (p.done / p.total) * 100 : 0} tone="bg-blue-600" /></div>}
      <div className="text-xs text-slate-500 mt-2">Reads the current version of every policy, finds the ones that speak to each item, and quotes the passage that establishes the process. Nothing closes until you accept it on the item.</div>
    </div>
  );
}

export default function OverviewTab({ boot, onRefresh, onGoItems, onGoTab }) {
  if (!boot.cycle) return <StartYear boot={boot} onRefresh={onRefresh} onGoTab={onGoTab} />;
  const ov = boot.overview || {};
  const t = ov.totals || {};
  const d = ov.decisions || {};
  return (
    <div className="space-y-6">
      <ReleaseStrip boot={boot} onRefresh={onRefresh} />
      <DatesCard boot={boot} onRefresh={onRefresh} />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Ready" value={`${t.pct || 0}%`} sub={`${t.ready || 0} of ${t.applicable || 0} items that apply`} />
        <Stat label="Closed by policy" value={t.by_policy || 0} sub="a policy already covers it" />
        <Stat label="Open with owners" value={t.open || 0} sub={`${t.overdue || 0} overdue · ${t.at_risk || 0} at risk`} />
        <Stat label="Departments on it" value={t.departments || 0} sub={`${t.people || 0} people across them`} />
        <Stat label="Needs my decision" value={(d.total || 0) + (boot.questions_open || 0)} sub={`proposals, submissions${boot.questions_open ? `, ${boot.questions_open} question${boot.questions_open === 1 ? '' : 's'}` : ''}`} tone={(d.total || boot.questions_open) ? 'border-orange-200 bg-orange-50/40' : ''} />
      </div>

      <QuestionsCard boot={boot} onRefresh={onRefresh} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="By section" right={<span className="text-xs text-slate-500">applies · ready · open</span>}>
          <div className="space-y-2.5">
            {(ov.sections || []).map((s) => {
              const pct = s.applicable ? Math.round((s.ready / s.applicable) * 100) : 0;
              return (
                <button key={s.section_letter} type="button" onClick={() => onGoItems({ section: s.section_letter })} className="w-full grid grid-cols-[28px_1fr_90px_110px] gap-3 items-center text-left text-sm hover:bg-slate-50 rounded-lg px-1 py-0.5">
                  <span className="font-bold text-slate-500">{s.section_letter}.</span>
                  <span className="truncate text-slate-800" title={`${s.section_letter}. ${s.section_title}`}>{s.section_title}</span>
                  <span className="text-xs text-slate-500 text-right tabular-nums">{s.applicable} · {s.ready} · {s.open}</span>
                  <Progress pct={pct} tone={s.applicable === 0 ? 'bg-slate-300' : 'bg-emerald-600'} />
                </button>
              );
            })}
          </div>
        </Card>

        <Card title="Needs my decision" right={<span className="text-xs text-slate-500">the agent proposes, you decide</span>}>
          {d.total ? (
            <div className="space-y-2 text-sm">
              {d.policy_proposals > 0 && <Row label="Policies found that may close an item" n={d.policy_proposals} onClick={() => onGoItems({ status: 'open' })} />}
              {d.submissions > 0 && <Row label="Owner submissions to review" n={d.submissions} onClick={() => onGoItems({ status: 'submitted' })} />}
            </div>
          ) : (
            <Empty>Nothing waiting on you. Proposals from the policy search and owner submissions land here.</Empty>
          )}
          <PolicySweep boot={boot} onRefresh={onRefresh} />
        </Card>
      </div>

      <Assistant boot={boot} initialMessages={boot.initial_assistant_messages || null} />

      <Card title="By department" right={<span className="flex items-center gap-3"><span className="text-xs text-slate-500">click a row to see its items</span>{boot.cycle?.id && <a className="text-xs font-semibold text-orange-800 underline whitespace-nowrap" href={downloadUrl(`/cycles/${boot.cycle.id}/export.xlsx?split=department`)} title="One workbook, one sheet per department, in the plan's format with the logo.">Excel, one sheet per department</a>}</span>}>
        {(ov.departments || []).length === 0 ? <Empty>No departments yet. Confirm the roster under Owners.</Empty> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                <th className="py-2 pr-3 font-semibold">Department</th><th className="py-2 pr-3 font-semibold">Accountable</th><th className="py-2 pr-3 font-semibold">People on it</th>
                <th className="py-2 pr-3 font-semibold">Items</th><th className="py-2 pr-3 font-semibold">Ready</th><th className="py-2 pr-3 font-semibold">Open</th><th className="py-2 pr-3 font-semibold">Overdue</th><th className="py-2 pr-3 font-semibold">Next due</th><th className="py-2 pr-3 font-semibold">Excel</th>
              </tr></thead>
              <tbody>
                {ov.departments.map((o, i) => (
                  <tr key={`${o.department_id || 'none'}-${o.owner_user_id || 'none'}-${i}`} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer" onClick={() => onGoItems(o.department_id ? { department: o.department_id } : { owner: o.owner_user_id || 'none' })}>
                    <td className="py-2 pr-3 font-semibold text-slate-800">{o.department_name}</td>
                    <td className="py-2 pr-3 text-slate-700">{(o.accountable_names || []).length ? o.accountable_names.join(', ') : o.owner_name || <span className="text-rose-700">No one</span>}</td>
                    <td className="py-2 pr-3 text-xs text-slate-600 max-w-[260px]">{(o.people || []).length ? o.people.join(', ') : '—'}</td>
                    <td className="py-2 pr-3 tabular-nums">{o.items}</td>
                    <td className="py-2 pr-3 tabular-nums text-emerald-700 font-semibold">{o.ready}</td>
                    <td className="py-2 pr-3 tabular-nums">{o.open}</td>
                    <td className="py-2 pr-3"><Pill tone={o.overdue ? 'bg-rose-50 text-rose-700 ring-rose-200' : 'bg-emerald-50 text-emerald-700 ring-emerald-200'}>{o.overdue}</Pill></td>
                    <td className="py-2 pr-3 text-slate-600">{o.next_due ? fmtDay(o.next_due) : '—'}</td>
                    <td className="py-2 pr-3">{o.department_id ? <a className="text-xs font-semibold text-orange-800 underline whitespace-nowrap" href={downloadUrl(`/cycles/${boot.cycle.id}/export.xlsx?department=${encodeURIComponent(o.department_id)}`)} onClick={(e) => e.stopPropagation()} title={`Every ${o.department_name} item, in the plan's format with the logo. Send it to the department or to a PBM.`}>Download</a> : <span className="text-xs text-slate-400">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function ReleaseStrip({ boot, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [notify, setNotify] = useState(false);
  const released = !!boot.cycle?.released_at;
  const notified = boot.cycle?.notified_at || null;
  const flip = async () => {
    setBusy(true); setMsg(null);
    const d = await api(`/cycles/${boot.cycle.id}/release`, { method: 'POST', body: { release: !released } });
    setBusy(false);
    if (!d?.success) { setMsg(d?.message || 'Could not change the release'); return; }
    onRefresh();
  };
  return (
    <div className={`flex items-center justify-between gap-3 flex-wrap rounded-2xl border px-4 py-3 text-sm ${released ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-orange-50 border-orange-200 text-slate-800'}`}>
      <span>
        {released
          ? <><b>Released to owners</b> on {fmtDay(boot.cycle.released_at)}. Owners see their items under My items. {notified ? <>Owners were notified on {fmtDay(notified)}; reminders run on the clock in Setup, only for what is still open.</> : <>Nothing has been emailed yet: press <b>Notify owners</b> to send each person one introduction listing their sections and subsections.</>}</>
          : <><b>Private to compliance.</b> Owners see nothing yet and nothing is sent. Review Setup, Owners and the items, then release the year.</>}
        {msg && <span className="text-rose-700 ml-2">{msg}</span>}
      </span>
      <span className="flex items-center gap-2">
        {released && <Button kind={notified ? 'secondary' : 'primary'} onClick={() => setNotify(true)} disabled={busy}>{notified ? 'Notify again' : 'Notify owners'}</Button>}
        <Button kind={released ? 'secondary' : 'primary'} onClick={flip} disabled={busy}>{released ? 'Take it back' : 'Release to owners'}</Button>
      </span>
      {notify && <NotifyModal boot={boot} onClose={() => setNotify(false)} onSent={onRefresh} />}
    </div>
  );
}

function Row({ label, n, onClick }) {
  return (
    <button type="button" onClick={onClick} className="w-full flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2 hover:bg-slate-50 text-left">
      <span className="text-slate-800">{label}</span>
      <Pill tone="bg-orange-50 text-orange-800 ring-orange-200">{n}</Pill>
    </button>
  );
}

function StartYear({ boot, onRefresh, onGoTab }) {
  const year = boot.edition.contract_year;
  const [due, setDue] = useState(`${year - 1}-12-15`);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const steps = [
    { n: 1, title: 'Tell the app what the plan is', done: boot.settings.exists, detail: boot.settings.exists ? 'Profile saved. The items that do not apply to this plan will be marked for you.' : 'Plan types, special needs plans, and a few yes or no switches. Two minutes.', action: () => onGoTab('setup'), label: boot.settings.exists ? 'Review profile' : 'Open Setup' },
    { n: 2, title: 'Confirm who owns each topic', done: boot.roster.confirmed, detail: boot.roster.rows ? (boot.roster.confirmed ? `${boot.roster.rows} topics confirmed.` : `${boot.roster.rows} topics proposed, ${boot.roster.review.length} to look at.`) : 'The app gives each of the 72 CMS topics to a department, with everyone in it and the lead accountable. You confirm once.', action: () => onGoTab('roster'), label: boot.roster.confirmed ? 'Review owners' : 'Open Owners' },
    { n: 3, title: `Open contract year ${year}`, done: false, detail: `${boot.edition.item_count} CMS items become your list, filtered to the plan, each with its owner and a due date.` },
  ];
  const open = async () => {
    setBusy(true); setMsg(null);
    const d = await api('/cycles', { method: 'POST', body: { contract_year: year, due_date: due } });
    setBusy(false);
    if (!d?.success) { setMsg(d?.message || 'Could not open the year'); return; }
    onRefresh();
  };
  return (
    <Card title={`Start contract year ${year}`} right={<span className="text-xs text-slate-500">CMS checklist dated {fmtDay(boot.edition.source?.cms_date)} · {boot.edition.item_count} items</span>}>
      <ol className="space-y-4">
        {steps.map((s) => (
          <li key={s.n} className="flex items-start gap-4">
            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${s.done ? 'bg-emerald-600 text-white' : 'bg-orange-50 text-orange-800 ring-1 ring-orange-200'}`}>{s.done ? '✓' : s.n}</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900">{s.title}</div>
              <div className="text-sm text-slate-600 mt-0.5">{s.detail}</div>
              {s.n < 3 && <div className="mt-2"><Button kind="secondary" onClick={s.action}>{s.label}</Button></div>}
              {s.n === 3 && (
                <div className="mt-2 flex items-end gap-3 flex-wrap">
                  <Field label="CMS deadline" className="w-48" hint="An internal due date can be set on Overview once the year is open."><input type="date" className={inputCls} value={due} onChange={(e) => setDue(e.target.value)} /></Field>
                  <Button onClick={open} disabled={busy || !boot.settings.exists || !boot.roster.rows}>{busy ? 'Opening…' : `Open ${year}`}</Button>
                  {(!boot.settings.exists || !boot.roster.rows) && <span className="text-xs text-slate-500 pb-2">Finish steps 1 and 2 first.</span>}
                </div>
              )}
              {s.n === 3 && msg && <div className="text-xs text-rose-600 mt-2">{msg}</div>}
            </div>
          </li>
        ))}
      </ol>
    </Card>
  );
}
